import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import ReportTableHeader from "./ReportTableHeader";
import Pagination from "./Pagination";
import NoRecords from "../alerts/NoRecords";
import RequestFailed from "../alerts/RequestFailed";
import { DASHBOARD_MESSAGES } from "../../constants";

const ReportTable = ({
    // Data and columns
    data,
    columns,
    // Table configuration
    columnOrder,
    setColumnOrder,
    columnVisibility,
    handleColumnVisibility,
    columnResizeMode = "onChange",
    // Sorting
    sorting,
    setSorting,
    getSortDetails,
    sortIcon,
    // Filtering
    columnFilter,
    handleColumnFilter,
    setPopupVisible,
    setAccessorKey,
    setColumnName,
    setColumnId,
    setIsPopupPosition,
    setFilterValue,
    setFilterTrigger,
    isPopupVisible,
    columnName,
    filterValue,
    // Column reordering
    reorderColumn,
    setCheckColumnOrder,
    // Pagination
    showPagination = true,
    pageCount,
    handlePageNumberClick,
    itemsPerPage,
    handleItemsPerPageChange,
    filteredDataLength,
    // SSN specific
    organizationsAccess,
    handleSsnDisplayMode,
    ssnDisplayMode,
    ssnValues,
    getSSNDisplay,
    // Loading states
    isFetchingData = false,
    isPaginating = false,
    // Custom renderers
    renderNoData,
    isError = false,
    // Styling
    tableContainerStyle = {},
    tableStyle = {},
    headerStyle = {},
    // Additional props
    className = "",
    manualSorting = false, // Add manual sorting support
    manualPagination = false, // Add manual pagination support
}) => {
    const topScrollbarRef = useRef(null);
    const tableContentRef = useRef(null);
    const bottomScrollbarRef = useRef(null);
    const topScrollBarContentRef = useRef(null);
    const bottomScrollBarContentRef = useRef(null);
    const headerContentRef = useRef(null);

    // Create table instance
    const table = useReactTable({
        data,
        columns,

        state: {
            sorting,
            columnOrder,
            columnVisibility,
        },

        // REQUIRED for resizing in v8
        enableColumnResizing: true,
        columnResizeMode: "onChange",

        onColumnVisibilityChange: handleColumnVisibility,
        onColumnOrderChange: setColumnOrder,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),

        manualSorting,
        manualPagination,
        pageCount: manualPagination ? pageCount : undefined,

        debugTable: false,
        debugHeaders: false,
        debugColumns: false,
    });

    const getTotalWidth = (columns) => {
        return columns
            .filter((column) => column.getIsVisible())
            .reduce((total, column) => {
                return total + (column.getSize() || 0);
            }, 0);
    };

    const activeColumnWidth = getTotalWidth(table.getAllColumns());
    const getCenterTotalSize = table.getCenterTotalSize();
    const iconAdjustmentWidth = 0;

    // Sync scrollbars
    useEffect(() => {
        const topScrollbar = topScrollbarRef.current;
        const bottomScroll = bottomScrollbarRef.current;
        const tableContent = tableContentRef.current;
        const headerContent = headerContentRef.current;

        if (!topScrollbar || !bottomScroll || !tableContent || !headerContent) {
            return;
        }

        const syncScrollFromTable = () => {
            const scrollLeftPos = tableContent.scrollLeft;
            headerContent.scrollLeft = scrollLeftPos;
            topScrollbar.scrollLeft = scrollLeftPos;
            bottomScroll.scrollLeft = scrollLeftPos;
        };

        const syncScrollFromHeaderScrollbar = () => {
            const scrollLeftPos = headerContent.scrollLeft;
            tableContent.scrollLeft = scrollLeftPos;
            topScrollbar.scrollLeft = scrollLeftPos;
            bottomScroll.scrollLeft = scrollLeftPos;
        };

        const syncScrollFromTopScrollbar = () => {
            const scrollLeftPos = topScrollbar.scrollLeft;
            tableContent.scrollLeft = scrollLeftPos;
            headerContent.scrollLeft = scrollLeftPos;
            bottomScroll.scrollLeft = scrollLeftPos;
        };

        const syncScrollFromBottomScrollbar = () => {
            const scrollLeftPos = bottomScroll.scrollLeft;
            tableContent.scrollLeft = scrollLeftPos;
            headerContent.scrollLeft = scrollLeftPos;
            topScrollbar.scrollLeft = scrollLeftPos;
        };

        tableContent.addEventListener("scroll", syncScrollFromTable);
        headerContent.addEventListener("scroll", syncScrollFromHeaderScrollbar);
        topScrollbar.addEventListener("scroll", syncScrollFromTopScrollbar);
        bottomScroll.addEventListener("scroll", syncScrollFromBottomScrollbar);

        // Initial width update
        const totalTableWidth =
            table.getCenterTotalSize() + iconAdjustmentWidth;
        topScrollbar.firstChild.style.width = `${totalTableWidth}px`;
        bottomScroll.firstChild.style.width = `${totalTableWidth}px`;

        // Cleanup event listeners on component unmount
        return () => {
            tableContent.removeEventListener("scroll", syncScrollFromTable);
            headerContent.removeEventListener(
                "scroll",
                syncScrollFromHeaderScrollbar
            );
            topScrollbar.removeEventListener(
                "scroll",
                syncScrollFromTopScrollbar
            );
            bottomScroll.removeEventListener(
                "scroll",
                syncScrollFromBottomScrollbar
            );
        };
    }, [table, isFetchingData, isPaginating]);

    const handleCondition = (condition, value1, value2) => {
        return condition ? value1 : value2;
    };

    const getCellContent = (cell, index) => {
        if (ssnValues?.[0] === "No Access") {
            return flexRender(cell.column.columnDef.cell, cell.getContext());
        }

        if (getSSNDisplay) {
            return getSSNDisplay(cell, index);
        }

        return flexRender(cell.column.columnDef.cell, cell.getContext());
    };

    return (
        <div className={className}>
            {/* Sticky Header with Top Scrollbar */}
            {!isFetchingData && (
                <div
                    className="sticky-filter-header custom-table-container"
                    style={headerStyle}
                >
                    {/* Top Scrollbar */}
                    <div
                        className="custom-topscrollbar"
                        ref={topScrollbarRef}
                        tabIndex={0}
                    >
                        <div
                            className="scrollbar-content"
                            style={{
                                width: getCenterTotalSize,
                                height: "15px",
                            }}
                            ref={topScrollBarContentRef}
                        ></div>
                    </div>

                    {/* Table Header */}
                    <div
                        ref={headerContentRef}
                        className="custom-table-content"
                    >
                        <table
                            style={{
                                width: getCenterTotalSize,
                                tableLayout: "fixed",
                                ...tableStyle,
                            }}
                        >
                            <thead id="reportingData">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <ReportTableHeader
                                                key={header.id}
                                                header={header}
                                                table={table}
                                                columnFilter={columnFilter}
                                                reorderColumn={reorderColumn}
                                                sorting={sorting}
                                                setSorting={setSorting}
                                                getSortDetails={getSortDetails}
                                                flexRender={flexRender}
                                                filteredDataLength={
                                                    filteredDataLength
                                                }
                                                setCheckColumnOrder={
                                                    setCheckColumnOrder
                                                }
                                                sortIcon={sortIcon}
                                                organizationsAccess={
                                                    organizationsAccess
                                                }
                                                ssnDisplayMode={ssnDisplayMode}
                                                handleSsnDisplayMode={
                                                    handleSsnDisplayMode
                                                }
                                                isFetchingReportData={
                                                    isFetchingData
                                                }
                                                data={data}
                                                ssnValues={ssnValues}
                                                setPopupVisible={
                                                    setPopupVisible
                                                }
                                                setAccessorKey={setAccessorKey}
                                                setColumnName={setColumnName}
                                                setColumnId={setColumnId}
                                                setIsPopupPosition={
                                                    setIsPopupPosition
                                                }
                                                setFilterValue={setFilterValue}
                                                setFilterTrigger={setFilterTrigger}
                                                isPopupVisible={isPopupVisible}
                                                columnName={columnName}
                                                filterValue={filterValue}
                                            />
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                        </table>
                    </div>
                </div>
            )}

            {/* Table Body */}
            {!isFetchingData && (
                <div
                    className="custom-table-container"
                    style={{
                        paddingBottom: filteredDataLength > 20 ? 0 : 16,
                        ...tableContainerStyle,
                    }}
                >
                    {/* Column Filters Display */}
                    {columnFilter.length > 0 && (
                        <div>
                            {activeColumnWidth < 1180 && (
                                <div className="horizontal-bar"></div>
                            )}
                            <div>
                                <div className="filters-applied-area">
                                    Filters applied:{" "}
                                    {columnFilter.map((filter, index) => (
                                        <div
                                            className="filters-applied-box"
                                            key={`${filter.columnName}-${filter.filterValue}`}
                                        >
                                            <span className="pr-10p">
                                                {filter.columnName}:{" "}
                                                <strong>
                                                    {filter.filterValue}
                                                </strong>
                                            </span>
                                            <button
                                                aria-label={`Clear ${filter.columnName} Filter`}
                                                className="filters-applied-clear-value"
                                                onClick={() => {
                                                    const updatedFilters =
                                                        columnFilter.filter(
                                                            (_, i) =>
                                                                i !== index
                                                        );
                                                    if (handleColumnFilter) {
                                                        handleColumnFilter(
                                                            updatedFilters.length >
                                                                0
                                                                ? updatedFilters
                                                                : []
                                                        );
                                                    }
                                                }}
                                            >
                                                <i className="fa-solid fa-x"></i>
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        className="filters-applied-clear-all"
                                        onClick={() => handleColumnFilter?.([])}
                                    >
                                        Clear All
                                    </button>
                                </div>
                                <div className="horizontal-bar"></div>
                            </div>
                        </div>
                    )}

                    {/* Table Content */}
                    <div className="custom-table-content" ref={tableContentRef}>
                        <table
                            style={{
                                width: getCenterTotalSize,
                                tableLayout: "fixed",
                                ...tableStyle,
                            }}
                        >
                            {!isError && (
                                <tbody>
                                    {handleCondition(
                                        filteredDataLength === 0 &&
                                            columnFilter.length > 0,
                                        <tr>
                                            <td
                                                colSpan={
                                                    table.getAllColumns().length
                                                }
                                                style={{ textAlign: "center" }}
                                            >
                                                No data matches the current
                                                filter.
                                            </td>
                                        </tr>,
                                        table
                                            .getRowModel()
                                            .rows.map((row, index) => (
                                                <tr
                                                    className={
                                                        (index % 2 === 0
                                                            ? "even "
                                                            : "odd ") +
                                                        (index === 0
                                                            ? "first "
                                                            : "")
                                                    }
                                                    style={{
                                                        borderBottom:
                                                            "1px solid #cdcdcd",
                                                    }}
                                                    key={row.id}
                                                >
                                                    {row
                                                        .getVisibleCells()
                                                        .map((cell) => (
                                                            <td
                                                                key={cell.id}
                                                                style={{
                                                                    minWidth:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .minSize,
                                                                    width: cell.column.getSize(),
                                                                    maxWidth:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .maxSize,
                                                                    textAlign:
                                                                        "left",
                                                                    wordBreak:
                                                                        "break-all",
                                                                    paddingLeft: 8,
                                                                    paddingRight: 15,
                                                                }}
                                                                className="td"
                                                            >
                                                                {getCellContent(
                                                                    cell,
                                                                    index
                                                                )}
                                                            </td>
                                                        ))}
                                                </tr>
                                            ))
                                    )}
                                </tbody>
                            )}
                        </table>

                        {/* Error Display takes priority over No Data */}
                        {isError ? (
                            <div
                                style={{
                                    paddingLeft: 24,
                                    paddingRight: 24,
                                    paddingBottom: 24,
                                }}
                            >
                                <RequestFailed
                                    tabIndex={0}
                                    message={DASHBOARD_MESSAGES.REQUEST_FAILED}
                                    label="reports"
                                    attribute1="Reports"
                                    attribute3="reports_error"
                                />
                            </div>
                        ) : (
                            renderNoData && (
                                <div
                                    style={{
                                        paddingLeft: 24,
                                        paddingRight: 24,
                                        paddingTop: 24,
                                    }}
                                >
                                    <NoRecords
                                        page="reports"
                                        label="reports"
                                        attribute1="reports"
                                        attribute3="reports_error"
                                    />
                                </div>
                            )
                        )}
                    </div>

                    {/* Bottom Scrollbar */}
                    <div
                        className="custom-topscrollbar"
                        ref={bottomScrollbarRef}
                        tabIndex={0}
                    >
                        <div
                            className="scrollbar-content"
                            style={{ width: getCenterTotalSize }}
                            ref={bottomScrollBarContentRef}
                        ></div>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {showPagination &&
                filteredDataLength > 20 &&
                !isFetchingData &&
                !isError && (
                    <div className="py-20p">
                        <Pagination
                            pageCount={pageCount}
                            gotoPage={(index) =>
                                handlePageNumberClick(index + 1)
                            }
                            setPageSize={handleItemsPerPageChange}
                            pageSize={itemsPerPage}
                            paginationClassName="pagination-table"
                            fieldName="report_detail"
                            pageName="custom_event"
                            options={[20, 50, 100]}
                        />
                    </div>
                )}
        </div>
    );
};

ReportTable.propTypes = {
    // Data and columns
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,

    // Table configuration
    columnOrder: PropTypes.array.isRequired,
    setColumnOrder: PropTypes.func.isRequired,
    columnVisibility: PropTypes.object.isRequired,
    handleColumnVisibility: PropTypes.func.isRequired,
    columnResizeMode: PropTypes.string,

    // Sorting
    sorting: PropTypes.shape({
        id: PropTypes.string,
        desc: PropTypes.bool,
        accessorKey: PropTypes.string,
        sortMethod: PropTypes.string,
    }).isRequired,
    setSorting: PropTypes.func.isRequired,
    getSortDetails: PropTypes.func,
    sortIcon: PropTypes.string.isRequired,

    // Filtering
    columnFilter: PropTypes.array.isRequired,
    handleColumnFilter: PropTypes.func,
    setPopupVisible: PropTypes.func.isRequired,
    setAccessorKey: PropTypes.func.isRequired,
    setColumnName: PropTypes.func.isRequired,
    setColumnId: PropTypes.func.isRequired,
    setIsPopupPosition: PropTypes.func.isRequired,
    setFilterValue: PropTypes.func.isRequired,
    setFilterTrigger: PropTypes.func,
    isPopupVisible: PropTypes.bool.isRequired,
    columnName: PropTypes.string,
    filterValue: PropTypes.string,

    // Column reordering
    reorderColumn: PropTypes.func.isRequired,
    setCheckColumnOrder: PropTypes.func,

    // Pagination
    showPagination: PropTypes.bool,
    pageCount: PropTypes.number,
    handlePageNumberClick: PropTypes.func,
    itemsPerPage: PropTypes.number,
    handleItemsPerPageChange: PropTypes.func,
    filteredDataLength: PropTypes.number.isRequired,

    // SSN specific
    organizationsAccess: PropTypes.object,
    handleSsnDisplayMode: PropTypes.func,
    ssnDisplayMode: PropTypes.number,
    ssnValues: PropTypes.array,
    getSSNDisplay: PropTypes.func,

    // Loading states
    isFetchingData: PropTypes.bool,
    isPaginating: PropTypes.bool,

    // Custom renderers
    renderNoData: PropTypes.bool,
    isError: PropTypes.bool,

    // Styling
    tableContainerStyle: PropTypes.object,
    tableStyle: PropTypes.object,
    headerStyle: PropTypes.object,
    className: PropTypes.string,

    // Manual modes
    manualSorting: PropTypes.bool,
    manualPagination: PropTypes.bool,
    };

export default ReportTable;